import { useState, useEffect, useMemo, useRef } from "react";
import Select, { StylesConfig } from "react-select";
import { countryDialCodes } from "../helpers/countryDialCodes";

type OptData = { value: string; label: string };

type OnChangeFunc = (opt: OptData) => void;

interface CountryDialSelectProps {
    defaultCountry?: string;
    disabled?: boolean;
    className?: string;
    classNamePrefix?: string;
    styles?: StylesConfig;
    menuPlacement?: "auto" | "top" | "bottom";
    onChange: OnChangeFunc;
}

export default function CountryDialSelect({
    defaultCountry = "at",
    disabled,
    className,
    classNamePrefix,
    styles,
    menuPlacement = "top",
    onChange,
}: CountryDialSelectProps) {
    const options = useMemo(() => (
        Object.entries(countryDialCodes).map(([code, dial]) => ({
            value: code,
            label: `+${dial} ${code.toUpperCase()}`,
        }))
    ), []);

    const defaultOption = useMemo(() => (
        options.find((option) => option.value === defaultCountry)
    ), [options, defaultCountry]);

    const [selectedCountry, setSelectedCountry] = useState<OptData | null | undefined>(defaultOption);

    const onChangeRef = useRef<OnChangeFunc>(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        setSelectedCountry(defaultOption ?? null);

        if (defaultOption && onChangeRef.current) {
            onChangeRef.current(defaultOption);
        }
    }, [defaultOption]);

    const handleChange = (option: OptData) => {
        setSelectedCountry(option);
        onChange?.(option);
    };

    return (
        <Select
            value={selectedCountry}
            isSearchable
            menuPlacement={menuPlacement}
            className={className}
            classNamePrefix={classNamePrefix}
            styles={styles}
            options={options}
            isDisabled={!!disabled}
            onChange={handleChange}
        />
    );
}
